import { useCallback, useImperativeHandle, useMemo, useState, type Ref } from "react";
import { useTranslation } from "react-i18next";
import { itemReferenceAt } from "../../../api/content/changeSetsApi";
import type { StepDefinition } from "../../../api/content/authoring/stepDecoders";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_HELP_TEXT_CLASS } from "../../../ui/styles/formStyles";
import { useHerePath, useThreadPreparation } from "../../threads/prepare";
import type { CommitBuilder } from "../../changeSets/changeSetChoice";
import AddToChangeSet from "../draft/AddToChangeSet";
import type { AuthoringOptions } from "../draft/authoringOptions";
import DefinitionPanel from "../draft/DefinitionPanel";
import { incompleteLanguages } from "../draft/bilingualTexts";
import ItemTextFields from "../draft/ItemTextFields";
import { derivingTargetOf, itemEditAt, savedReference, useDerivationRoot, type ItemTarget } from "../draft/itemEdits";
import { definitionPath, useDefinitionCheck, type DefinitionCheck } from "../draft/useDefinitionCheck";
import { useUnsavedDraft } from "../draft/useUnsavedDraft";
import SimilarItemsPanel from "../similar/SimilarItemsPanel";
import AgentStepFields from "./AgentStepFields";
import Section from "../../../ui/forms/Section";
import HumanStepFields from "./HumanStepFields";
import { newPromptEdit, NO_PROMPT, promptDraftOf, type PromptDraft } from "./promptDraft";
import { newStepDefinition, STEP_FORM_KINDS, stepContentOf, withAgentInstructions, withKind } from "./stepDefinitions";
import ToolStepFields from "./ToolStepFields";
import SegmentedControl from "../../../ui/navigation/SegmentedControl";
import { stepKindText } from "../../workflows";

/** The step a form starts from: a stored version, or nothing for a new step. */
export interface StepBase {
  readonly reference: string;
  readonly itemId: string;
  readonly definition: StepDefinition;
}

interface StepFormProps {
  projectId: string;
  options: AuthoringOptions;
  base: StepBase | null;
  /** Called with the change set and the step's version in it once it is added. */
  onAdded?: (changeSetId: string, stepReference: string | null) => void | Promise<void>;
  /** The change set the save starts in: the Workflow editor's, so its copies stay together (D-8b). */
  changeSetId?: string | null;
  /** Lets the enclosing drawer route its close action through this draft guard. */
  closeRef?: Ref<{ requestClose: (close: () => void) => void }>;
}

/**
 * One form for agent, human and tool steps (AU-4, mockup `new-step.html`): a local draft,
 * checked by the server as it changes, previewed as the stored file, and added to a change
 * set for review. Nothing is saved before that.
 */
export default function StepForm({ projectId, options, base, onAdded, closeRef, changeSetId = null }: StepFormProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<StepDefinition>(() => base?.definition ?? newStepDefinition("tool"));
  const [prompt, setPrompt] = useState<PromptDraft>(() => (base?.definition.kind === "agent" ? promptDraftOf(base.definition.prompt) : NO_PROMPT));
  const [instructions, setInstructions] = useState<PromptDraft | null>(() => (base?.definition.kind === "agent" && typeof base.definition.agent_instructions === "string" ? promptDraftOf(base.definition.agent_instructions) : null));
  const chosenWithin = useDerivationRoot();
  const [target] = useState<ItemTarget>(() => derivingTargetOf(base, chosenWithin));
  const preparation = useThreadPreparation();
  const here = useHerePath();
  const content = useMemo(() => stepContentOf(step), [step]);
  const { connectSaver, markSaved, requestClose } = useUnsavedDraft({ step, prompt, instructions }, closeRef === undefined ? 0 : 1);
  useImperativeHandle(closeRef, () => ({ requestClose }));
  const serverCheck = useDefinitionCheck(projectId, content, base === null ? null : target.itemId);
  // A new prompt is written in the same change set: the server cannot see it yet.
  const check: DefinitionCheck = serverCheck.status === "checked" && prompt.kind === "new" ? { ...serverCheck, problems: serverCheck.problems.filter((problem) => definitionPath(problem) !== "prompt") } : serverCheck;

  const blockedBy = step.name.trim() === "" ? t("authoring.step.blocked.name") : incompleteLanguages(step.translations).length > 0 ? t("authoring.bilingual.blocked") : step.kind === "tool" && step.tool === "" ? t("authoring.step.blocked.tool") : step.kind === "agent" && prompt.kind === "existing" && prompt.reference === "" ? t("authoring.step.blocked.prompt") : null;

  const instructionsName = t("authoring.step.agent.instructionsName", { name: step.name });
  const commits = useCallback((): readonly CommitBuilder[] => {
    const created = step.kind === "agent" ? [newPromptEdit(prompt, step.name), newPromptEdit(instructions, instructionsName)].flatMap((edit) => (edit === null ? [] : [edit])) : [];
    if (step.kind !== "agent" || created.length === 0) {
      return [async (head) => [await itemEditAt(projectId, head, target, stepContentOf(step))]];
    }
    // New prompts first, then the step that pins the versions their commit wrote.
    const pinned = async (head: string | null, draft: PromptDraft | null, chosen: string | null) =>
      draft?.kind === "new" ? (head === null ? null : await itemReferenceAt(projectId, head, draft.itemId)) : chosen;
    return [
      async (head) => Promise.all(created.map((edit) => itemEditAt(projectId, head, { itemId: edit.item_id, base: null }, edit.content))),
      async (head) => {
        const pinnedStep = withAgentInstructions({ ...step, prompt: (await pinned(head, prompt, step.prompt)) ?? "" }, await pinned(head, instructions, step.agent_instructions ?? null));
        return [await itemEditAt(projectId, head, target, stepContentOf(pinnedStep))];
      },
    ];
  }, [projectId, prompt, instructions, instructionsName, step, target]);

  const changeAgentStep = (next: StepDefinition, nextPrompt: PromptDraft) => {
    setStep(next);
    setPrompt(nextPrompt);
  };

  // A chosen version is pinned at once, so the server checks it; a new prompt is pinned by the save.
  // The choice may land after a read: it changes the pin only, never edits made meanwhile.
  const changeInstructions = (next: PromptDraft | null) => {
    setInstructions(next);
    const pin = next?.kind === "existing" ? next.reference : null;
    setStep((current) => (current.kind === "agent" ? withAgentInstructions(current, pin) : current));
  };

  const added = async (changeSetId: string) => {
    if (onAdded !== undefined) {
      await savedReference(projectId, changeSetId, target).then(
        (reference) => onAdded(changeSetId, reference),
        () => onAdded(changeSetId, null),
      );
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedControl
            label={t("authoring.step.kind")}
            options={STEP_FORM_KINDS.map((kind) => ({ key: kind, label: stepKindText(kind, t), disabled: base !== null && step.kind !== kind }))}
            selected={step.kind}
            onSelect={(kind) => { setStep(withKind(step, kind)); }}
            size="regular"
          />
          <span className="app-text-muted flex-1 text-xs">{t(`authoring.step.kindHints.${step.kind}`)}</span>
          <AppButton variant="ghost" size="sm" onClick={() => { preparation.prepare({ kind: "author", projectId, returnTo: here, itemKind: "step", draft: content, name: step.name }); }}>
            {t("authoring.step.withAgent")}
          </AppButton>
        </div>
        <Section title={t("authoring.step.about")}>
          <ItemTextFields item={step} nameLabel={t("authoring.step.name")} descriptionLabel={t("authoring.step.description")} check={check} onChange={setStep} />
        </Section>
        {step.kind === "tool" ? <ToolStepFields step={step} options={options} check={check} onChange={setStep} /> : null}
        {step.kind === "agent" ? (
          <AgentStepFields
            step={step}
            prompt={prompt}
            instructions={instructions}
            options={options}
            check={check}
            onChange={changeAgentStep}
            onInstructions={changeInstructions}
          />
        ) : null}
        {step.kind === "human" ? <HumanStepFields step={step} options={options} check={check} onChange={setStep} /> : null}
      </div>
      <div className="space-y-4">
        {/* The stored file holds the step's own fields; the item content wraps them. */}
        <SimilarItemsPanel projectId={projectId} kind="step" name={step.name} description={step.description} excludeItemId={base?.itemId} />
        <DefinitionPanel content={step} base={base?.definition ?? null} check={check} />
        <AddToChangeSet
          ref={connectSaver}
          projectId={projectId}
          defaultTitle={t(base === null ? "authoring.step.addTitle" : "authoring.step.editTitle", { name: step.name.trim() || t("authoring.step.unnamed") })}
          commits={commits}
          blockedBy={blockedBy}
          onSaved={markSaved}
          onAdded={added}
          initialChangeSetId={changeSetId}
          // How items, versions and Workflows relate is Help's (OW-12); only what changes for others stays:
          // in the Workflow editor's drawer its save repins the draft (D-8); a derive chosen within a
          // Workflow updates that Workflow in the change set (PK-12); otherwise pins stay.
          notice={base === null ? undefined : <p className={FORM_HELP_TEXT_CLASS}>{t(`authoring.step.versionNotice.${onAdded !== undefined ? "inWorkflow" : target.base !== null && target.derivation?.chosen === true ? "inRoot" : "pinned"}`)}</p>}
        />
      </div>
    </div>
  );
}
